import React, { useEffect, useRef, useState } from 'react';
import './App.css';
import { useScrollAnimations } from './ScrollAnimations';
import { useFullPageScroll } from './FullPageScroll';
import emailjs from '@emailjs/browser';
import Articles from './Articles';

function App() {
  useScrollAnimations();
  useFullPageScroll();
  const mallVideoRef = useRef<HTMLVideoElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Initialize EmailJS with your public key
  useEffect(() => {
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    if (publicKey) {
      emailjs.init(publicKey);
    } else {
      console.error('EmailJS public key not found in environment variables');
    }
  }, []);
  
  useEffect(() => {
    const video = mallVideoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // Play video when 50% of it is visible
            video.play().catch(err => {
              console.log('Video autoplay failed:', err);
            });
          }
        });
      },
      {
        threshold: 0.5 // Trigger when 50% of the video is visible
      }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, []);
  
  const scrollToSection = (sectionId: string) => {
    const targetElement = document.getElementById(sectionId);
    if (targetElement) {
      // Calculate the offset position
      const offsetTop = targetElement.offsetTop;
      
      // Scroll to the target
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
      
      // Update nav dots if they exist
      const sections = ['hero', 'technology', 'features', 'about', 'beta', 'articles', 'contact'];
      const index = sections.indexOf(sectionId);
      if (index !== -1) {
        const dots = document.querySelectorAll('.nav-dot');
        dots.forEach((dot, i) => {
          if (i === index) {
            dot.classList.add('active');
          } else {
            dot.classList.remove('active');
          }
        });
      }
    }
  };
  
  return (
    <div className="App">
      <nav className="navbar">
        <div className="nav-container">
          <img src="/images/logo.png" alt="HERBA" className="logo-image" />
          <ul className="nav-links">
            <li><a href="#technology">Technology</a></li>
            <li><a href="#features">Features</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#articles">Articles</a></li>
            <li><a href="#beta" className="nav-cta">Join Beta</a></li>
          </ul>
        </div>
      </nav>
      
      <header className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h2 className="hero-title">Intelligent. Automated.<br />Landscaping</h2>
            <p className="hero-subtitle">A revolutionary way to keep spaces effortlessly green - saving you time, water, and money</p>
            <div className="hero-buttons">
              <button 
                className="cta-button primary" 
                onClick={() => {
                  // Use setTimeout to ensure DOM is ready
                  setTimeout(() => {
                    const element = document.getElementById('technology');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
              >
                Our Technology
              </button>
              <button 
                className="cta-button secondary" 
                onClick={() => {
                  // Use setTimeout to ensure DOM is ready
                  setTimeout(() => {
                    const element = document.getElementById('beta');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
              >
                Join Our Beta
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="sensor-container">
              <div className="cell-pulses">
                <div className="cell-ring"></div>
                <div className="cell-ring"></div>
                <div className="cell-ring"></div>
                <div className="cell-ring"></div>
              </div>
              <img src="/images/sensor.png" alt="Herba Smart Sensor" className="sensor-image" />
            </div>
          </div>
        </div>
      </header>
      
      <div className="video-overlay">
        <video 
          className="scroll-video"
          src="/images/sensor-scroll.mp4"
          muted
          playsInline
          preload="auto"
          // style={{ borderRadius: '12px' }}
        />
      </div>
      
      <nav className="section-nav">
        <div className="nav-dot active" data-section="hero">
          <span className="nav-tooltip">Home</span>
        </div>
        <div className="nav-dot" data-section="technology">
          <span className="nav-tooltip">Technology</span>
        </div>
        <div className="nav-dot" data-section="features">
          <span className="nav-tooltip">Features</span>
        </div>
        <div className="nav-dot" data-section="about">
          <span className="nav-tooltip">About</span>
        </div>
        <div className="nav-dot" data-section="beta">
          <span className="nav-tooltip">Beta</span>
        </div>
        <div className="nav-dot" data-section="articles">
          <span className="nav-tooltip">Articles</span>
        </div>
        <div className="nav-dot" data-section="contact">
          <span className="nav-tooltip">Contact</span>
        </div>
      </nav>
      
      <main className="content-wrapper">
        <div className="hero-spacer"></div>
        
        <section id="technology" className="technology-section">
          <div className="tech-layout">
            <div className="tech-content">
              <h2 className="section-title fade-up">Commercial Landscaping Maintenance Reimagined</h2>
              <div className="tech-description fade-up">
                <p>Patented smart sensors and watering systems that simplify maintenance and protect your landscaping investment.</p>
                {/* <div className="tech-stats-list" style={{ marginTop: '1.5rem' }}>
                  <div className="tech-stat-item">
                    <span className="stat-number">62%</span>
                    <span className="stat-text">of landscape companies struggle to hire crews</span>
                  </div>
                </div> */}
                {/* <h3><br></br>Plants don't care about labor shortages. <br></br> Herba protects your plants, and budget.</h3> */}
                <div className="tech-stats-list" style={{ marginTop: '1.5rem' }}>
                  <div className="tech-stat-item">
                    <span className="stat-number">50%</span>
                    <span className="stat-text">reduction in water usage</span>
                  </div>
                  <div className="tech-stat-item">
                    <span className="stat-number">25-50%</span>
                    <span className="stat-text">fewer maintenance visits needed</span>
                  </div>
                  <div className="tech-stat-item">
                    <span className="stat-number">100%</span>
                    <span className="stat-text">visibility into plant health</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="tech-spacer">
              <div className="mobile-video-container">
                <video 
                  className="mobile-tech-video"
                  src="/images/sensor-scroll.mp4"
                  muted
                  playsInline
                  autoPlay
                  loop
                />
              </div>
            </div>
          </div>
      </section>
      
      <section id="features" className="features-section">
        <div className="container">
          <h2 className="section-title fade-up">Smart Features</h2>
          <div className="features-grid">
            <div className="feature-card fade-up">
              <h3>Automated Watering</h3>
              <p>Sub-irrigation delivers water directly to roots for healthier plants and less waste</p>
            </div>
            <div className="feature-card fade-up">
              <h3>Dashboard Access</h3>
              <p>Monitor and control water levels, conditions, and plant health from anywhere</p>
            </div>
            <div className="feature-card fade-up">
              <h3>Solar Charging</h3>
              <p>Solar-powered system runs continuously without interruption</p>
            </div>
            <div className="feature-card fade-up">
              <h3>Fits In Any Pot</h3>
              <p>Modular watering system adapts to any planter size or location</p>
            </div>
            <div className="feature-card fade-up">
              <h3>GPS & Fleet Management</h3>
              <p>Track and manage multiple sites efficiently with real-time location data</p>
            </div>
            <div className="feature-card fade-up">
              <h3>Notifications</h3>
              <p>Receive alerts for water levels and maintenance needs via email</p>
            </div>
          </div>
        </div>
      </section>
      
      <section id="about" className="about-section">
        <div className="container">
          <div className="about-layout">
            <div className="about-video">
              <video 
                ref={mallVideoRef}
                className="mall-video"
                src="/images/mall.mp4"
                muted
                playsInline
                preload="auto"
              />
            </div>
            <div className="about-content">
              <h2 className="section-title fade-up">Fully Customizable to Fit Your Needs</h2>
              <p className="about-text fade-up">Herba automates plant care beyond traditional irrigation. Oversee unlimited locations from a single dashboard that gives you real-time visibility into landscape health and maintenance needs across all properties.</p>

              {/* <div className="stats-grid"> */}
                {/* <div className="stat scale-in">
                  <h3>95%</h3>
                  <p>Plant survival rate</p>
                </div> */}
                {/* <div className="stat scale-in">
                  <h3>50%</h3>
                  <p>Water savings</p>
                </div>
                <div className="stat scale-in">
                  <h3>25%</h3>
                  <p>Reduction in time on site (est)</p>
                </div>
                <div className="stat scale-in">
                  <h3>24/7</h3>
                  <p>Continuous monitoring</p>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </section>
      
      <section id="beta" className="preorder-section">
        <div className="container">
          <h2 className="section-title fade-up">Request to Join the Herba Beta</h2>
          <p className="section-subtitle fade-up">Be among the first 30 to experience the future of automated plant care and maintenance.</p>
          {/* <div className="beta-benefits"> */}
            {/* <h3>What you'll receive as a beta tester:</h3>
            <ul className="beta-benefits-list">
              <li>The Herba smart sensor</li>
              <li>Custom sub-irrigation system to fit your needs</li>
              <li>A $200 stipend for purchasing materials</li>
              <li>Free dashboard access for monitoring</li>
              <li>Priority support and onboarding assistance</li>
            </ul> */}
            <button className="cta-button primary beta-button" onClick={() => document.getElementById('beta-modal')?.classList.add('active')}>
              Request Beta Access
            </button>
          {/* </div> */}
          {/* <div className="preorder-grid">
            <div className="preorder-card fade-up">
              <div className="preorder-image">
                <img src="/images/sensor.png" alt="Herba Sensor Only" />
              </div>
              <div className="preorder-content">
                <h3>Sensor Only</h3>
                <div className="price">$299</div>
                <ul className="features-list">
                  <li>1 Herba Sensor</li>
                  <li>Know when and how much to water your plants</li>
                  <li>Dashboard access + Notifications + 1 year warranty</li>

                </ul>
                <button className="cta-button primary">Preorder Now</button>
              </div>
            </div> */}
            
            {/* <div className="preorder-card fade-up">
              <div className="preorder-image">
                <img src="/images/delta_main.jpg" alt="Herba Starter Kit" />
              </div>
              <div className="preorder-content">
                <h3>Modern Luxury</h3>
                <div className="price">
                  <span className="price-prefix">Starting at</span>
                  $749
                </div>
                <ul className="features-list">
                  <li>1 Pre-assembled system</li>
                  <li>Includes modern planter pot</li>
                  <li>Water reservoir included</li>
                  <li>Herba Sensor + Dashboard access + 1 year warranty</li>
                </ul>
                <button className="cta-button primary">Preorder Now</button>
              </div>
            </div> */}
            {/* 
            <div className="preorder-card fade-up">
              <div className="preorder-image">
                <img src="/images/downtown_main.jpg" alt="Herba Office Pack" />
              </div>
              <div className="preorder-content">
                <h3>Fully Assembled</h3>
                <div className="price">
                  <span className="price-prefix">Starting at</span>
                  $699
                </div>
                <ul className="features-list">
                  <li>1 Pre-assembled system</li>
                  <li>Includes statement planter</li>
                  <li>Water reservoir included</li>
                  <li>Herba Sensor + Dashboard access + 1 year warranty</li>
                </ul>
                <button className="cta-button primary">Preorder Now</button>
              </div>
            </div> */}
            
            {/* <div className="preorder-card featured fade-up">
              <div className="featured-badge">Most Popular</div>
              <div className="preorder-image">
                <img src="/images/wilshire_main.jpg" alt="Herba Home Bundle" />
              </div>
              <div className="preorder-content">
                <h3>Outdoor </h3>
                <div className="price">
                  <span className="price-prefix">Starting at</span>
                  $1200
                </div>
                <ul className="features-list">
                <li>1 Pre-assembled system</li>
                  <li>Includes statement planter</li>
                  <li>Water reservoir included</li>
                  <li>Dashboard access + Notifications + 2 year warranty</li>
                </ul>
                <button className="cta-button primary">Preorder Now</button>
              </div>
            </div> */}
            {/* 
            <div className="preorder-card fade-up">
              <div className="preorder-content full-width">
                <h3>Custom Development</h3>
                <div className="price">
                  <span className="price-prefix">Starting at</span>
                  $1,299
                </div>
                <ul className="features-list">
                  <li>Custom fit water reservoirs for your needs</li>
                  <li>Dashboard access + Notifications + 2 year warranty</li>

                  <li>Dashboard access + Notifications + 2 year warranty</li>
                </ul>
                <button className="cta-button primary">Preorder Now</button>
              </div>
            </div>
            
        
            
          </div> */}
        </div>
      </section>
      
      <Articles />
      
      <footer id="contact" className="footer contact-section">
        <div className="footer-wrapper">
          <div className="container">
            <div className="footer-content">
              <div className="footer-brand">
                <h2 className="footer-logo">HERBA</h2>
                <p>Intelligent plant care for the modern world</p>
              </div>
              <div className="footer-links">
                <h4>Product</h4>
                <ul>
                  <li><a href="#">Technology</a></li>
                  <li><a href="#">Features</a></li>
                  <li><a href="#">Pricing</a></li>
                </ul>
              </div>
              <div className="footer-links">
                {/* <h4>Company</h4>
                <ul>
                  <li><a href="#">About</a></li>
                  <li><a href="#">Careers</a></li>
                  <li><a href="#">Press</a></li>
                </ul> */}
              </div>
              <div className="footer-contact">
                <h4>Contact</h4>
                <a href="mailto:support@herba-grown.com" className="footer-contact-link">Contact us</a>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2024 Herba Technologies. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
      </main>
      
      {/* Beta Access Modal */}
      <div id="beta-modal" className="modal">
        <div className="modal-overlay" onClick={() => {
          document.getElementById('beta-modal')?.classList.remove('active');
          setShowSuccess(false);
        }}></div>
        <div className="modal-content">
          <button className="modal-close" onClick={() => {
            document.getElementById('beta-modal')?.classList.remove('active');
            setShowSuccess(false);
          }}>×</button>
          {!showSuccess ? (
            <>
              <h2>Request Beta Access</h2>
              <p>Request to join our exclusive beta and help shape the future of commercial landscaping.</p>
              <form className="beta-form" onSubmit={async (e) => {
            e.preventDefault();
            setIsSubmitting(true);
            
            const form = e.currentTarget;
            const formData = {
              name: (form.elements.namedItem('name') as HTMLInputElement).value,
              email: (form.elements.namedItem('email') as HTMLInputElement).value,
              business: (form.elements.namedItem('business') as HTMLInputElement).value,
              location: (form.elements.namedItem('location') as HTMLInputElement).value,
            };
            
            try {
              const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
              if (!serviceId) {
                throw new Error('EmailJS service ID not configured');
              }
              
              await emailjs.send(
                serviceId,
                'template_uzcpdcg',
                {
                  to_email: 'support@herba-grown.com',
                  from_name: formData.name,
                  from_email: formData.email,
                  business_name: formData.business,
                  location: formData.location,
                  reply_to: formData.email,
                }
              );
              
              form.reset();
              setShowSuccess(true);
            } catch (error) {
              console.error('Failed to send email:', error);
              alert('Sorry, there was an error submitting your request. Please try again or email us directly at support@herba-grown.com');
            } finally {
              setIsSubmitting(false);
            }
          }}>
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input type="text" id="name" name="name" required />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input type="email" id="email" name="email" required />
            </div>
            <div className="form-group">
              <label htmlFor="business">Business/Organization Name *</label>
              <input type="text" id="business" name="business" required />
            </div>
            <div className="form-group">
              <label htmlFor="location">Location (City, State) *</label>
              <input type="text" id="location" name="location" required />
            </div>
            <button type="submit" className="cta-button primary" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Submit Request'}
            </button>
          </form>
            </>
          ) : (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <h2>Thank You!</h2>
              <p>Your beta request has been successfully submitted. We'll be in touch soon with next steps.</p>
              <button className="cta-button primary" onClick={() => {
                document.getElementById('beta-modal')?.classList.remove('active');
                setShowSuccess(false);
              }}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
